import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

export default function About() {
  const { t } = useLang();

  return (
    <section id="about" className="relative overflow-hidden py-28 lg:py-40" data-testid="about-section">
      {/* Full-bleed background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/about-image.jpeg"
          alt="MOBIX wheelchair"
          draggable="false"
          className="h-full w-full object-cover"
          data-testid="about-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-mobix-navy/90 via-mobix-navy/70 to-mobix-navy/40" />
      </div>

      <div className="relative z-10 max-w-[1800px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 lg:items-center">
        {/* Left column — text */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="text-[13px] font-bold tracking-[0.24em] uppercase text-mobix-orange mb-7">
            {t.about.overline}
          </p>
          <h2
            className="font-extrabold uppercase tracking-tight text-white text-[clamp(1.875rem,4.8vw,3.75rem)] leading-[1.06] break-words"
            data-testid="about-title"
          >
            {t.about.title1}
            <br />
            {t.about.title2}
            <span className="text-mobix-orange">{t.about.titleAccent}</span>.
          </h2>
          <p className="mt-9 max-w-md text-base sm:text-lg font-medium text-white/85 leading-relaxed">
            {t.about.paragraph}
          </p>
        </motion.div>

        {/* Right column — timeline */}
        <div className="relative">
          <div className="flex flex-col">
            {t.about.steps.map((step, i) => {
              const isLast = i === t.about.steps.length - 1;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, ease: EASE, delay: i * 0.1 }}
                  data-testid={`about-step-${i}`}
                  className="flex gap-6"
                >
                  {/* marker column: circle + connecting line */}
                  <div className="flex flex-col items-center">
                    <span className="mt-1.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 border-mobix-orange bg-mobix-navy" />
                    {!isLast && <span className="mt-1 w-[2px] flex-1 bg-mobix-orange/80" />}
                  </div>

                  {/* content */}
                  <div className={isLast ? "pb-0" : "pb-14"}>
                    <h3 className="text-lg sm:text-xl font-extrabold uppercase tracking-wide text-white leading-none">
                      {step.title}
                    </h3>
                    <span className="mt-4 block h-[3px] w-9 bg-mobix-orange" aria-hidden="true" />
                    <p className="mt-5 max-w-[22rem] text-[15px] sm:text-base font-medium text-white/80 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
